/**
 * Managing who can sign in. Platform administrators only.
 *
 * An invitation returns a link exactly once — there is no way to read it again — so the
 * page makes copying it the obvious next action rather than something to notice later.
 */

import { Fragment, useState } from "react";

import { ApiError } from "@/api/client";
import type { AdminUser, Invitation } from "@/api/endpoints";
import { endpoints } from "@/api/endpoints";
import { Badge, Empty, ErrorState, Field, Loading, Notice, RelativeTime } from "@/components/ui";
import { useAsync } from "@/hooks/useAsync";
import { useSession } from "@/hooks/useSession";

function LinkPanel({ issued, onDone }: { issued: Invitation; onDone: () => void }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(issued.invitation_url);
      setCopied(true);
    } catch {
      // Clipboard access can be refused; the link is on screen to select by hand.
      setCopied(false);
    }
  };

  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">Send this link to {issued.user.email}</span>
        <button type="button" className="button button--small button--ghost" onClick={onDone}>
          Done
        </button>
      </div>
      <div className="card__body stack" style={{ gap: 12 }}>
        <Notice tone="warning" title="Shown once">
          This link is not stored anywhere readable. If you lose it, issue a new one — you
          cannot look this one up again.
        </Notice>

        <input
          className="input mono"
          readOnly
          value={issued.invitation_url}
          onFocus={(event) => event.target.select()}
        />

        <div className="row">
          <button type="button" className="button button--primary" onClick={copy}>
            {copied ? "Copied" : "Copy link"}
          </button>
          <span className="muted" style={{ fontSize: 12.5 }}>
            Expires <RelativeTime iso={issued.expires_at} />
          </span>
        </div>
      </div>
    </div>
  );
}

function InviteForm({ onIssued }: { onIssued: (issued: Invitation) => void }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();

  if (!open) {
    return (
      <button type="button" className="button button--primary" onClick={() => setOpen(true)}>
        Invite someone
      </button>
    );
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);
    try {
      const issued = await endpoints.inviteUser({
        email,
        display_name: displayName,
        is_platform_admin: isAdmin,
      });
      setOpen(false);
      setEmail("");
      setDisplayName("");
      setIsAdmin(false);
      onIssued(issued);
    } catch (caught) {
      setProblem(caught instanceof ApiError ? caught.message : (caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card" onSubmit={submit}>
      <div className="card__header">
        <span className="card__title">Invite someone</span>
      </div>
      <div className="card__body stack" style={{ gap: 14 }}>
        {problem && <Notice tone="danger">{problem}</Notice>}

        <Notice tone="info">
          They choose their own password from the link. Inviting them creates the account;
          giving them a role on a project is a separate step in that project's settings.
        </Notice>

        <div className="row" style={{ gap: 12, alignItems: "flex-end" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Field label="Email" required htmlFor="invite-email">
              <input
                id="invite-email"
                className="input"
                type="email"
                value={email}
                required
                placeholder="someone@appen.com"
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <Field label="Name" htmlFor="invite-display">
              <input
                id="invite-display"
                className="input"
                value={displayName}
                placeholder="Grace Hopper"
                onChange={(event) => setDisplayName(event.target.value)}
              />
            </Field>
          </div>
        </div>

        <div className="checkbox-row">
          <input
            id="invite-admin"
            type="checkbox"
            checked={isAdmin}
            onChange={(event) => setIsAdmin(event.target.checked)}
          />
          <label htmlFor="invite-admin">
            <span className="field__label">Platform administrator</span>
            <div className="field__hint">
              Can see every project, invite people, and read the whole audit trail.
            </div>
          </label>
        </div>

        <div className="row">
          <button type="submit" className="button button--primary" disabled={busy}>
            Send invitation
          </button>
          <button type="button" className="button button--ghost" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}

function State({ user }: { user: AdminUser }) {
  if (!user.is_active) return <Badge>Deactivated</Badge>;
  if (!user.has_password) return <Badge tone="warning">Invitation pending</Badge>;
  if (user.locked_until && new Date(user.locked_until) > new Date()) {
    return <Badge tone="warning">Locked</Badge>;
  }
  if (user.must_change_password) return <Badge tone="warning">Must change password</Badge>;
  return <Badge tone="success">Active</Badge>;
}

function InvitationHistory({ userId }: { userId: string }) {
  const { data, loading } = useAsync(() => endpoints.userInvitations(userId), [userId]);

  if (loading) return <Loading rows={1} label="Loading invitations" />;
  const invitations = data ?? [];
  if (invitations.length === 0) {
    return <div className="faint" style={{ fontSize: 12.5 }}>No invitations issued.</div>;
  }

  return (
    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5 }} className="muted">
      {invitations.map((invitation) => (
        <li key={invitation.id}>
          Issued <RelativeTime iso={invitation.created_at} /> —{" "}
          {invitation.accepted_at ? (
            <>
              accepted <RelativeTime iso={invitation.accepted_at} />
            </>
          ) : invitation.revoked_at ? (
            "replaced by a newer link"
          ) : new Date(invitation.expires_at) < new Date() ? (
            "expired"
          ) : (
            <>
              open, expires <RelativeTime iso={invitation.expires_at} />
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

export function Users() {
  const { me } = useSession();
  const [issued, setIssued] = useState<Invitation>();
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState<string>();
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<string>();

  const { data, error, loading, reload } = useAsync(
    () => endpoints.users({ limit: 200, search: search || undefined }),
    [search],
  );

  const act = async (id: string, action: () => Promise<unknown>) => {
    setBusy(id);
    setProblem(undefined);
    try {
      await action();
      reload();
    } catch (caught) {
      setProblem(caught instanceof ApiError ? caught.message : (caught as Error).message);
    } finally {
      setBusy(undefined);
    }
  };

  if (loading) return <Loading rows={4} label="Loading accounts" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const users = data?.items ?? [];

  return (
    <>
      <div className="page-header">
        <div>
          <h1>People</h1>
          <div className="page-header__meta">
            {users.length} {users.length === 1 ? "account" : "accounts"}
          </div>
        </div>
      </div>

      <div className="stack">
        {problem && <Notice tone="danger">{problem}</Notice>}

        {issued && <LinkPanel issued={issued} onDone={() => { setIssued(undefined); reload(); }} />}

        <InviteForm onIssued={(next) => { setIssued(next); reload(); }} />

        <div className="row">
          <input
            className="input"
            style={{ maxWidth: 320 }}
            type="search"
            value={search}
            placeholder="Search by name or email"
            aria-label="Search accounts"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        {users.length === 0 ? (
          <div className="card">
            <Empty title="No accounts yet">Invite someone to get started.</Empty>
          </div>
        ) : (
          <div className="card">
            <div className="table__scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>State</th>
                    <th>Projects</th>
                    <th>Last signed in</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <Fragment key={user.id}>
                    <tr>
                      <td>
                        <div style={{ fontWeight: 550 }}>
                          {user.display_name}
                          {user.is_platform_admin && (
                            <>
                              {" "}
                              <Badge tone="accent">Admin</Badge>
                            </>
                          )}
                        </div>
                        <div className="faint" style={{ fontSize: 12 }}>
                          {user.email}
                        </div>
                      </td>
                      <td>
                        <State user={user} />
                      </td>
                      <td className="muted">{user.project_count}</td>
                      <td className="muted">
                        <RelativeTime iso={user.last_login_at} />
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <div className="row" style={{ justifyContent: "flex-end" }}>
                          <button
                            type="button"
                            className="button button--small"
                            disabled={busy === user.id}
                            onClick={() =>
                              act(user.id, async () =>
                                setIssued(await endpoints.resetUserPassword(user.id)),
                              )
                            }
                          >
                            {user.has_password ? "Reset password" : "New link"}
                          </button>

                          {user.id !== me?.id && (
                            <>
                              <button
                                type="button"
                                className="button button--small"
                                disabled={busy === user.id}
                                onClick={() =>
                                  act(user.id, () =>
                                    endpoints.updateUser(user.id, {
                                      is_platform_admin: !user.is_platform_admin,
                                    }),
                                  )
                                }
                              >
                                {user.is_platform_admin ? "Remove admin" : "Make admin"}
                              </button>

                              <button
                                type="button"
                                className={`button button--small${user.is_active ? " button--danger" : ""}`}
                                disabled={busy === user.id}
                                onClick={() =>
                                  act(user.id, () =>
                                    endpoints.updateUser(user.id, { is_active: !user.is_active }),
                                  )
                                }
                              >
                                {user.is_active ? "Deactivate" : "Reactivate"}
                              </button>
                            </>
                          )}

                          <button
                            type="button"
                            className="button button--small button--ghost"
                            onClick={() =>
                              setExpanded(expanded === user.id ? undefined : user.id)
                            }
                          >
                            {expanded === user.id ? "Hide" : "History"}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expanded === user.id && (
                      <tr>
                        <td colSpan={5} style={{ background: "var(--surface-sunken)" }}>
                          <div style={{ fontWeight: 550, marginBottom: 4, fontSize: 12.5 }}>
                            Invitations for {user.email}
                          </div>
                          <InvitationHistory userId={user.id} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
